import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type { DevicesRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface DeviceOption {
  readonly value: string;
  readonly label: string;
}

export interface UseDeviceOptionsResult {
  readonly options: readonly DeviceOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated device options for the `Combobox` async mode --
 * mirrors useBranchOptions/useCompanyOptions. `accountId` scopes the list to
 * one company's devices for a staff caller; a client caller is always
 * limited to their own account by the backend regardless, so it's
 * meaningless -- and should stay `undefined` -- for them (same rule
 * useBranchOptions documents). Labelled by IMEI, matching the target-device
 * picker's Figma.
 */
export function useDeviceOptions(
  repository: DevicesRepository | undefined,
  accountId: string | undefined,
): UseDeviceOptionsResult {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number) => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc" as const,
    sortBy: "imei" as const,
    status: "ACTIVE" as const,
    ...(accountId === undefined ? {} : { accountId }),
    ...(search === undefined ? {} : { search }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.devices.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy: "imei",
      status: "ACTIVE",
      accountId,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((device) => ({ value: device.id, label: device.imei })),
      ),
    [query.data],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
